import { FeatureflagStatus, type FeatureflagId } from '#graphql-operations'
import { useLocalStorage } from '@vueuse/core'

type FeatureFlagIdType = `${FeatureflagId}`

type Featureflag = {
  id: FeatureFlagIdType
  label: string
  enabled: boolean
}

type UseFeatureFlag = {
  toggleOverride: (id: FeatureFlagIdType) => void
  resetOverrides: () => void
  features: ComputedRef<Featureflag[]>
}

export function useFeatureFlag(): UseFeatureFlag

/**
 * Determine whether the given feature is enabled.
 *
 * To create a new feature flag, go to:
 * https://zuerich.ddev.site/de/admin/structure/feature-flags
 * and add a new flag. Then run `drush cex` and `drush cr`. Afterwards,
 * restart Nuxt (and possibly refresh your IDE), so the newly added feature
 * flag appears in the enum/type.
 */
export function useFeatureFlag(
  /**
   * The ID of the feature flat.
   */
  id: FeatureFlagIdType,

  /**
   * The default value that is used when:
   * - The feature flag does not exist
   * - No decision has been made yet (which is the case after the first deployment).
   *
   * Defaults to `false`.
   */
  defaultValue?: boolean,
): ComputedRef<boolean>

export function useFeatureFlag(
  id?: FeatureFlagIdType,
  defaultValue?: boolean,
): ComputedRef<boolean> | UseFeatureFlag {
  const data = useInitData()
  const enabledViaOverride = useLocalStorage<FeatureFlagIdType[]>(
    'featureFlagsOverride',
    () => [],
  )

  // Overload where an ID is provided as an argument.
  if (id) {
    return computed(() => {
      if (enabledViaOverride.value.includes(id)) {
        return true
      }

      // Try to find a featureflag.
      const flag = data.value.featureflags.find((v) => v.enum === id)

      if (flag) {
        if (flag.featureStatus === FeatureflagStatus.enabled) {
          return true
        } else if (flag.featureStatus === FeatureflagStatus.disabled) {
          return false
        }
      }

      // Either no flag exists or the status has not been set yet.
      return defaultValue || false
    })
  }

  function toggleOverride(id: FeatureFlagIdType) {
    if (enabledViaOverride.value.includes(id)) {
      enabledViaOverride.value = enabledViaOverride.value.filter(
        (overrideId) => overrideId !== id,
      )
    } else {
      enabledViaOverride.value.push(id)
    }
  }

  function resetOverrides() {
    enabledViaOverride.value = []
  }

  const features = computed(() => {
    return data.value.featureflags
      .filter((v) => v.featureStatus === FeatureflagStatus.disabled)
      .map((v) => {
        const enabled =
          v.featureStatus === FeatureflagStatus.enabled ||
          enabledViaOverride.value.includes(v.enum)
        return {
          id: v.enum,
          enabled,
          label: v.label || v.enum,
        }
      })
  })

  return {
    toggleOverride,
    resetOverrides,
    features,
  }
}
